import { Injectable, OnDestroy } from '@angular/core'
import {
  Image,
  ImagePath,
  University,
  UniversityAbstract,
  UniversityService as UniversityApiService,
  UniversityList,
  UniversityUser,
} from '@sciper/api-client'
import { BehaviorSubject, Subject, takeUntil } from 'rxjs'
import { environment } from 'src/environments/environment'
import {
  AbstractRestService,
  RestServiceStatus,
} from '../abstract-rest/abstract-rest.service'
import { SelectedImage } from '../image/image.fixtures'
import { ImageService } from '../image/image.service'

export type UniversityUserList = {
  universityId: string
  users: UniversityUser[]
}

export type UniversityPageOptions = {
  page: number
  size: number
}

export const DEFAULT_PAGE_SIZE = 20

@Injectable({
  providedIn: 'root',
})
export class UniversityService
  extends AbstractRestService
  implements OnDestroy
{
  private _destroySubject$: Subject<void> = new Subject<void>()

  universities: BehaviorSubject<UniversityAbstract[]> = new BehaviorSubject<
    UniversityAbstract[]
  >([])
  university: BehaviorSubject<University> = new BehaviorSubject<University>(
    undefined
  )
  universityUsers: BehaviorSubject<UniversityUserList> =
    new BehaviorSubject<UniversityUserList>(undefined)

  constructor(
    private universityApiService: UniversityApiService,
    private imageService: ImageService
  ) {
    super()
  }

  override ngOnDestroy() {
    super.ngOnDestroy()
    this._destroySubject$.next()
    this._destroySubject$.complete()
  }

  fetchUniversity(universityId: string) {
    if (!universityId) {
      return
    }

    const universityObservable =
      this.universityApiService.getUniversity(universityId)
    universityObservable
      .pipe(takeUntil(this._destroySubject$))
      .subscribe((newUniversity) => {
        if (newUniversity !== this.university.value) {
          this.university.next(newUniversity)
        }
      })
  }

  fetchUniversityList(
    pageOptions: UniversityPageOptions = {
      page: 0,
      size: DEFAULT_PAGE_SIZE,
    }
  ) {
    this.status.next(RestServiceStatus.Loading)
    this.universityApiService
      .getUniversityList(
        undefined,
        pageOptions?.page ?? 0,
        pageOptions?.size ?? DEFAULT_PAGE_SIZE
      )
      .pipe(takeUntil(this._destroySubject$))
      .subscribe(
        (universityList: UniversityList) => {
          const currentUniversities = this.universities.value ?? []
          if (currentUniversities.length < DEFAULT_PAGE_SIZE) {
            // replace previous universities due to reloading page 0
            this.universities.next(universityList.items)
          } else {
            // append to previous universities
            const newUniversities = universityList.items.filter(
              (newUniversity) =>
                currentUniversities.findIndex(
                  (university) =>
                    university.universityId === newUniversity.universityId
                ) < 0
            )
            currentUniversities.push(...newUniversities)
            this.universities.next(currentUniversities)
          }

          this.status.next(RestServiceStatus.Success)
        },
        (error) => {
          if (this.isNetworkError(error)) {
            this.status.next(RestServiceStatus.NetworkError)
          } else {
            this.status.next(RestServiceStatus.Error)
          }
        }
      )
  }

  fetchUniversityUserList(
    universityId: string,
    pageOptions: UniversityPageOptions = {
      page: 0,
      size: DEFAULT_PAGE_SIZE,
    }
  ) {
    if (!universityId) {
      return
    }
    this.status.next(RestServiceStatus.Loading)
    this.universityApiService
      .getUniversityUserList(
        universityId,
        pageOptions?.page ?? 0,
        pageOptions?.size ?? DEFAULT_PAGE_SIZE
      )
      .pipe(takeUntil(this._destroySubject$))
      .subscribe(
        (userList: UniversityUser[]) => {
          const currentUniversityUsers = this.universityUsers.value?.users ?? []
          if (
            universityId !== this.universityUsers.value?.universityId ||
            currentUniversityUsers.length < DEFAULT_PAGE_SIZE
          ) {
            // replace previous universityUsers due to new id or reloading page 0
            this.universityUsers.next({
              universityId,
              users: userList,
            })
          } else {
            // append to previous universityUsers
            const newUsers = userList.filter(
              (newUser) =>
                currentUniversityUsers.findIndex(
                  (user) => user.userId === newUser.userId
                ) < 0
            )
            currentUniversityUsers.push(...newUsers)
            this.universityUsers.next({
              universityId,
              users: currentUniversityUsers,
            })
          }
          this.status.next(RestServiceStatus.Success)
        },
        (error) => {
          if (this.isNetworkError(error)) {
            this.status.next(RestServiceStatus.NetworkError)
          } else {
            this.status.next(RestServiceStatus.Error)
          }
          this.universityUsers.next({
            universityId,
            users: [],
          })
        }
      )
  }

  async updateUniversity(
    university: University,
    callback: ((success: boolean) => void) | undefined = undefined
  ) {
    this.status.next(RestServiceStatus.Loading)
    this.universityApiService
      .updateUniversity(university.universityId, university)
      .pipe(takeUntil(this._destroySubject$))
      .subscribe(
        (_) => {
          this.status.next(RestServiceStatus.Success)
          if (callback) {
            callback(true)
          }
        },
        (error) => {
          if (this.isNetworkError(error)) {
            this.status.next(RestServiceStatus.NetworkError)
          } else {
            this.status.next(RestServiceStatus.Error)
          }
          if (callback) {
            callback(false)
          }
        }
      )
  }

  async updateUniversityWithImage(
    university: University,
    images: {
      squaredImage: SelectedImage | undefined
      image: SelectedImage | undefined
    },
    callback: ((success: boolean) => void) | undefined = undefined
  ) {
    if (!images?.squaredImage && !images?.image) {
      await this.updateUniversity(university, callback)
      return
    }

    const results = await Promise.all([
      this.imageService.createImage(
        images.squaredImage?.path,
        images.squaredImage?.file
      ),
      this.imageService.createImage(images.image?.path, images.image?.file),
    ])

    if (results.length > 0 && results[0]) {
      const squaredImage: Image = {
        alt: university.name,
        url: environment.apiBasePath + results[0].imagePath,
      }
      university.squaredImage = squaredImage
    }

    this.updateUniversity(university, (success: boolean) => {
      if (!success) {
        if (callback) {
          callback(false)
        }
        return
      }

      if (results.length > 1 && results[1]) {
        const image: Image = {
          alt: university.name,
          url: environment.apiBasePath + results[1].imagePath,
        }
        this.createUniversityImage(
          image,
          university.universityId,
          (imageSuccess: boolean) => {
            if (imageSuccess && callback) {
              callback(true)
            }
          }
        )
      }
    })
  }

  async createUniversityWithImage(
    university: University,
    imagePath: string,
    imageFile: File,
    callback: ((success: boolean) => void) | undefined = undefined
  ) {
    this.imageService.createImage(
      imagePath,
      imageFile,
      (success: boolean, imagePath: ImagePath) => {
        if (success && imagePath) {
          const image: Image = {
            alt: university.name,
            url: environment.apiBasePath + imagePath.imagePath,
          }

          this.createUniversity(
            university,
            (success: boolean, universityId: string) => {
              if (success && universityId) {
                this.createUniversityImage(image, universityId, callback)
              }
            }
          )
        }
      }
    )
  }

  async createUniversity(
    university: University,
    callback:
      | ((success: boolean, universityId: string) => void)
      | undefined = undefined
  ) {
    this.status.next(RestServiceStatus.Loading)
    this.universityApiService
      .createUniversity(university)
      .pipe(takeUntil(this._destroySubject$))
      .subscribe(
        (result) => {
          this.status.next(RestServiceStatus.Success)
          if (callback) {
            callback(true, result)
          }
        },
        (error) => {
          if (this.isNetworkError(error)) {
            this.status.next(RestServiceStatus.NetworkError)
          } else {
            this.status.next(RestServiceStatus.Error)
          }
          if (callback) {
            callback(false, undefined)
          }
        }
      )
  }

  async createUniversityImage(
    image: Image,
    universityId: string,
    callback: ((success: boolean) => void) | undefined = undefined
  ) {
    this.status.next(RestServiceStatus.Loading)
    this.universityApiService
      .createUniversityImage(universityId, image)
      .pipe(takeUntil(this._destroySubject$))
      .subscribe(
        (_) => {
          this.status.next(RestServiceStatus.Success)
          if (callback) {
            callback(true)
          }
        },
        (error) => {
          if (this.isNetworkError(error)) {
            this.status.next(RestServiceStatus.NetworkError)
          } else {
            this.status.next(RestServiceStatus.Error)
          }
          if (callback) {
            callback(false)
          }
        }
      )
  }

  private createImage(image: SelectedImage) {
    this.imageService.createImage(
      image.path,
      image.file,
      (success: boolean, imagePath: ImagePath) => {
        // TODO:
      }
    )
  }
}
